// src/components/modals/ModalPeriodosCerradosAdmin.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Row, Col, Spinner } from 'react-bootstrap';
import DataTable, { TableColumn } from 'react-data-table-component';
import NominaServices from '../../../Services/Catalogos/Nomina';
import DTOPeriodoCerrado from '../../../DTO/DTOPeriodoCerrado';

interface ModalPeriodosCerradosAdminProps {
  show: boolean;
  onHide: () => void;

  /** 🔹 Modo selección (reportes, consulta) */
  onSelectPeriodo?: (inicio: string, fin: string) => void;

  /** 🔹 Si es true NO permite reabrir */
  modoSoloConsulta?: boolean;

  /** 🔹 Rango inicial */
  initialFechaInicio?: string;
  initialFechaFin?: string;

  /** 🔥 SOLO necesarios si se va a REABRIR */
  idSupervisorPropio?: number;
  idUsuario?: number;
}


const formatDate = (date?: string | null) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

const formatDateTime = (date?: string | null) => {
  if (!date) return '-';
  return new Date(date).toLocaleString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};


const ModalPeriodosCerradosAdmin: React.FC<ModalPeriodosCerradosAdminProps> = ({
  show,
  onHide,
  onSelectPeriodo,
  modoSoloConsulta = false,
  initialFechaInicio = '',
  initialFechaFin = '',
  idSupervisorPropio,
  idUsuario,
}) => {
  // ---------------- ESTADOS ----------------
  const [fechaInicio, setFechaInicio] = useState(initialFechaInicio);
  const [fechaFin, setFechaFin] = useState(initialFechaFin);
  
  const [periodos, setPeriodos] = useState<DTOPeriodoCerrado[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtro, setFiltro] = useState('');

  const [periodoParaReabrir, setPeriodoParaReabrir] =
    useState<DTOPeriodoCerrado | null>(null);
  const [motivoReapertura, setMotivoReapertura] = useState('');
  const [procesando, setProcesando] = useState(false);

  // ---------------- EFECTOS ----------------
  useEffect(() => {
  if (!show) return;

  const inicio = initialFechaInicio || '';
  const fin = initialFechaFin || '';

  setFechaInicio(inicio);
  setFechaFin(fin);

  if (inicio && fin) {
    cargarPeriodosConFechas(inicio, fin);
  }
}, [show, initialFechaInicio, initialFechaFin]);


  // ---------------- DATA ----------------
  const cargarPeriodos = async () => {
    if (!fechaInicio || !fechaFin) return;

    setLoading(true);
    try {
      const res = await NominaServices.listarPeriodosCerrados(
        fechaInicio,
        fechaFin
      );
      setPeriodos(res.data || []);
    } catch (e) {
      console.error(e);
      setPeriodos([]);
    } finally {
      setLoading(false);
    }
  };

  const cargarPeriodosConFechas = async (inicio: string, fin: string) => {
  setLoading(true);
  try {
    const res = await NominaServices.listarPeriodosCerrados(inicio, fin);
    setPeriodos(res.data || []);
  } catch (e) {
    console.error(e);
    setPeriodos([]);
  } finally {
    setLoading(false);
  }
  
};





const filteredPeriodos = useMemo(() => {
  if (!filtro.trim()) return periodos;
  const s = filtro.toLowerCase();

  return periodos.filter(p =>
    [
      p.idSupervisor.toString(),
      p.nombreSupervisor,
      p.estadoPeriodo,
      p.cerradoPorNombre,
      p.motivoReapertura
    ]
      .join(' ')
      .toLowerCase()
      .includes(s)
  );
}, [periodos, filtro]);


const columns: TableColumn<DTOPeriodoCerrado>[] = [
  {
    name: 'ID',
    selector: row => row.idSupervisor ?? 0,
    sortable: true,
    width: '70px',
  },
  {
    name: 'Supervisor',
    selector: row => row.nombreSupervisor,
    sortable: true,
    width: '220px',
  },
  {
  name: 'Periodo',
  selector: row =>
    `${formatDate(row.fechaInicioPeriodo)} al ${formatDate(row.fechaFinPeriodo)}`,
  sortable: true,
  width: '220px',
},

  {
    name: 'Estado',
    selector: row => row.estadoPeriodo,
    sortable: true,
    width: '130px',
    cell: row => (
      <span
        className={
          row.estadoPeriodo === 'CERRADO'
            ? 'text-success fw-bold'
            : row.estadoPeriodo === 'REABIERTO'
            ? 'text-warning fw-bold'
            : 'text-danger fw-bold'
        }
      >
        {row.estadoPeriodo}
      </span>
    ),
  },
  {
    name: 'Cerrado por',
    selector: row => row.cerradoPorNombre ?? '-',
    sortable: true,
    width: '200px',
  },
  {
    name: 'Fecha Cierre',
    selector: row =>
      row.fechaCierre
        ? new Date(row.fechaCierre).toLocaleString('es-MX')
        : '-',
    sortable: true,
    width: '180px',
  },
  {
    name: 'Acción',
    cell: row => (
      <>
        {!modoSoloConsulta &&
          row.periodoCerrado &&
          row.estadoPeriodo === 'CERRADO' && (
            <Button
              size="sm"
              variant="outline-success"
              onClick={() => {
                setPeriodoParaReabrir(row);
                setMotivoReapertura('');
              }}
            >
              Reabrir
            </Button>
          )}
      </>
    ),
    center: true,
    ignoreRowClick: true,
    button: true,
    width: '160px',
  },
];


   const triggerNominaRefresh = () => {
        localStorage.setItem('nomina_refresh_trigger', Date.now().toString());
        console.log("Refresh disparado via localStorage");
    };

  // ---------------- RENDER ----------------
  return (
    <>
      {/* ================= MODAL PRINCIPAL ================= */}
    

<Modal
  show={show}
  onHide={onHide}
  size="xl"
  centered
  scrollable
>


    
        <Modal.Header closeButton className="bg-primary text-white py-2">
  <Modal.Title style={{ fontSize: '14px', fontWeight: 600 }}>
    📅 Periodos de Nómina (Noministas)
  </Modal.Title>
</Modal.Header>


        <Modal.Body className="bg-light">
          <Row className="mb-3 g-3 align-items-end">
            <Col md={5}>
              <Form.Label className="small fw-bold">Desde</Form.Label>
              <Form.Control
                type="date"
                value={fechaInicio}
                onChange={e => setFechaInicio(e.target.value)}
              />
            </Col>
            <Col md={5}>
              <Form.Label className="small fw-bold">Hasta</Form.Label>
              <Form.Control
                type="date"
                value={fechaFin}
                onChange={e => setFechaFin(e.target.value)}
              />
            </Col>
            <Col md={2}>
              <Button
                size="sm"
                className="w-100"
                onClick={cargarPeriodos}
                disabled={loading}
              >
                {loading ? <Spinner animation="border" size="sm" /> : 'Buscar'}
              </Button>
            </Col>
          </Row>

          <Form.Control
            size="sm"
            className="mb-3"
            placeholder="Buscar…"
            value={filtro}
            onChange={e => setFiltro(e.target.value)}
          />

          <DataTable
            columns={columns}
            data={filteredPeriodos}
            progressPending={loading}
            pagination
            dense
            striped
            highlightOnHover
            noDataComponent="No se encontraron periodos"
          />
        </Modal.Body>

        <Modal.Footer>
          <Button size="sm" variant="secondary" onClick={onHide}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ================= CONFIRMACIÓN REAPERTURA ================= */}
      {!modoSoloConsulta && (
        <Modal
          show={!!periodoParaReabrir}
          onHide={() => setPeriodoParaReabrir(null)}
          centered
        >
          <Modal.Header closeButton className="bg-success text-white">
            <Modal.Title className="fs-6 fw-bold">
              Reabrir Periodo
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            
            <p className="fw-bold">
  ¿Reabrir periodo del{' '}
  {periodoParaReabrir?.fechaInicioPeriodo} al{' '}
  {periodoParaReabrir?.fechaFinPeriodo}?
</p>


            <Form.Group>
              <Form.Label className="fw-bold text-danger">
                Motivo *
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={motivoReapertura}
                onChange={e => setMotivoReapertura(e.target.value)}
              />
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setPeriodoParaReabrir(null)}
            >
              Cancelar
            </Button>
            

            
<Button
  size="sm"
  variant="success"
  disabled={!motivoReapertura.trim() || procesando}
  onClick={async () => {
    if (
      !periodoParaReabrir ||
      !idUsuario
    ) {
      alert('Faltan datos para reabrir el periodo');
      return;
    }

    setProcesando(true);
    try {
      await NominaServices.gestionarCierrePeriodo(
        2,
        // CAMBIA ESTO:
        periodoParaReabrir.idSupervisor, 
        
        periodoParaReabrir.fechaInicioPeriodo,
        periodoParaReabrir.fechaFinPeriodo,
        idUsuario,
        motivoReapertura.trim()
      );

      setPeriodoParaReabrir(null);
      setMotivoReapertura('');
      cargarPeriodos();
      triggerNominaRefresh();
    } catch (e) {
      console.error(e);
      alert('Error al reabrir el periodo');
    } finally {
      setProcesando(false);
    }
  }}
>
  Sí, Reabrir
</Button>


          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default ModalPeriodosCerradosAdmin;
